import { Dialog, DialogContent, DialogContentText, DialogTitle, List, ListItem } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlySitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MonthlySite } from '../../../../services/apiSLTP/sites/sites';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDate } from '../../../../utils/dates';
import { crudStatus, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { getColorByPercentage, getMaterialStateColor } from '../../../../utils/colors';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import Holidays from 'date-holidays';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import dayjs from 'dayjs';
const hd = new Holidays('FR');

interface MonthlySitesTableProps {
    currentDate: Date;
}

const MonthlySitesTable: React.FC<MonthlySitesTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedUsers, setSelectedUsers] = useState<MonthlySite['dailySiteByDate'][number]['users'] | null>(null);

    const { data: dataMonthlySites, isLoading: isMonthlySitesLoading } = useMonthlySitesQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlySites) return [];

        return dataMonthlySites.sort((a, b) => {
            /*if (
                formatName(a.site.conducId?.firstName, a.site.conducId?.firstName) >
                formatName(b.site.conducId?.firstName, b.site.conducId?.firstName)
            )
                return -1;
            if (
                formatName(a.site.conducId?.firstName, a.site.conducId?.firstName) <
                formatName(b.site.conducId?.firstName, b.site.conducId?.firstName)
            )
                return 1;*/
            return a.site.name.localeCompare(b.site.name);
        });
    }, [dataMonthlySites, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailySiteByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlySite>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'site.name',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) => {
                    const status = params?.data?.site.crudStatus;
                    if (status === crudStatus.DELETED) return { color: muiTheme.palette.error.main };
                    if (status === crudStatus.INACTIVE) return { color: muiTheme.palette.action.disabled };
                    return undefined;
                },
            },
            {
                headerName: capitalizeFirstLetter(t('workTravelZone')),
                field: 'site.workTravelZoneId',
                colId: 'workTravelZone',
                pinned: 'left',
                valueFormatter: (params) => params.value?.shortName || '',
                cellRenderer: (params: ICellRendererParams<MonthlySite>) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                width: 25,
                                height: 25,
                                borderRadius: '50%',
                                backgroundColor: params.data && params.data?.site.workTravelZoneId.travelPrice > 0 ? 'gold' : 'white',
                                flexShrink: 0,
                                color: '#000',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                            }}
                        >
                            {params.value.shortName}
                        </div>
                    );
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA.minDistance - valueB.minDistance;
                },
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'site.codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },
            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailySiteByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailySiteByDate.length)) + 5,
                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },
                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlySite>[] = daysInMonth.map(({ date, label }) => ({
            headerName: label,
            colId: label,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailySiteByDate.find((value) => isSameDay(new Date(value.date), date))?.users.length,
            cellRenderer: (params: ICellRendererParams<MonthlySite>) => {
                const currentSiteDay = params.data?.dailySiteByDate.find((value) => isSameDay(new Date(value.date), date));
                return currentSiteDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentSiteDay.users.length.toString()} stateColor={''} showDot={false} />
                        {!!currentSiteDay.users.length && <IconButton iconName="infoSharp" onClick={() => setSelectedUsers(currentSiteDay.users)} />}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlySite>[] =
            user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: 'site._id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`production-days-sites-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlySitesLoading}
            />
            <Dialog open={!!selectedUsers} onClose={() => setSelectedUsers(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('users'))}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <List>
                            {selectedUsers?.map((daily, i) => (
                                <ListItem key={i} className="gap-5px fx-r">
                                    <span className="font-w-700">{formatName(daily.firstName, daily.lastName)}</span>
                                </ListItem>
                            ))}
                        </List>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlySitesTable;
